import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Tag, Filter, Search, Calendar } from 'lucide-react';
import { LoadingSpinner, EmptyState } from '../UI/Card';
import type { Discount, DiscountCategory } from '../../types/database';

const CATEGORIES: { value: DiscountCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'food', label: 'Food & Dining' },
  { value: 'retail', label: 'Retail' },
  { value: 'services', label: 'Services' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'education', label: 'Education' },
  { value: 'health', label: 'Health & Wellness' },
  { value: 'other', label: 'Other' },
];

export function Discounts() {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DiscountCategory | 'all'>('all');

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const { data, error} = await supabase
        .from('discounts')
        .select(`
          *,
          vendor:profiles(full_name, organization)
        `)
        .eq('is_active', true)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDiscounts(data || []);
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDiscounts = discounts.filter((discount) => {
    const matchesSearch = discount.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      discount.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || discount.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen">
      <div className="glass-card bg-gradient-to-r from-orange-500 to-red-500 text-white py-12 mx-4 mt-4 mb-6 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <Tag className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">Local Discounts</h1>
          </div>
          <p className="text-orange-100 text-lg">
            Discover exclusive deals from local businesses in your community
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="glass-card p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search discounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="glass-input w-full pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as DiscountCategory | 'all')}
                className="glass-input"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filteredDiscounts.length === 0 ? (
          <EmptyState
            icon={<Tag className="w-full h-full" />}
            title="No discounts found"
            description="Try adjusting your search or filters"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDiscounts.map((discount) => (
              <div
                key={discount.id}
                className="glass-card overflow-hidden group"
              >
                <div className="relative h-48 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 overflow-hidden">
                  {discount.image_url ? (
                    <img
                      src={discount.image_url}
                      alt={discount.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Tag className="w-16 h-16 text-orange-400 dark:text-orange-500" />
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                      {discount.discount_amount}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 line-clamp-1">
                        {discount.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        by {discount.vendor?.organization || discount.vendor?.full_name || 'Local Business'}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                    {discount.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Tag className="w-4 h-4 mr-2" />
                      <span className="capitalize">{discount.category}</span>
                    </div>
                    {discount.expiry_date && (
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>Valid until {new Date(discount.expiry_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {discount.terms && (
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 mb-4">
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                        <span className="font-semibold">Terms:</span> {discount.terms}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {discount.valid_for.map((userType) => (
                      <span
                        key={userType}
                        className="px-2 py-1 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 rounded-md text-xs font-medium"
                      >
                        {userType}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
